/**
 * Admin email-delivery panel: pick a course, pick an email (the cohort
 * "Access Opens" system message or any scheduled/sent message), see exactly
 * who has a delivery receipt, and resend to one person at a time.
 *
 * Built so the ops team can answer "Mary says she never got the access-opens
 * email" without an engineer. Manual UI sends write no per-recipient
 * receipts, so for those we show "included in the manual send" instead of a
 * per-person timestamp.
 */
import { useState } from 'preact/hooks';
import { defRoute, LoadedProps, RouteLoadProps } from '@components/router';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { showToast } from '@components/toaster';
import { Button } from '@components/buttons';
import { Spinner } from '@components/spinner';
import { compactDate } from 'shared/dateutil';

import { RenewalReminders } from './renewal-reminders';
import { ReminderCopy } from './reminder-copy';

type Courses = RpxResponse<typeof rpx.emailDelivery.getDeliveryCourses>;
type Emails = RpxResponse<typeof rpx.emailDelivery.getDeliveryEmails>;
type Recipients = RpxResponse<typeof rpx.emailDelivery.getDeliveryRecipients>;

const ACCESS_OPENS_KEY = 'deferredActivation';

async function load(_route: RouteLoadProps) {
  // The renewal cohorts query is the new, heavier one. If it fails it must not
  // take down the course-email tab, which is the resend tool ops relies on for
  // "I never got the access-opens email" tickets.
  const [courses, cohortResult] = await Promise.all([
    rpx.emailDelivery.getDeliveryCourses(),
    rpx.emailDelivery.getRenewalCohorts().then(
      (renewalCohorts) => ({ renewalCohorts, loadError: false }),
      () => ({ renewalCohorts: [], loadError: true }),
    ),
  ]);
  return { courses, ...cohortResult };
}

export const route = defRoute({
  load,
  authLevel: 'admin',
  Page,
});

function Page({ state }: LoadedProps<typeof load>) {
  const [tab, setTab] = useState<'course' | 'renewal' | 'reminderCopy'>('course');
  const [courseId, setCourseId] = useState('');
  const [emails, setEmails] = useState<Emails | undefined>(undefined);
  const [emailKey, setEmailKey] = useState('');
  const [recipientData, setRecipientData] = useState<Recipients | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [resendingId, setResendingId] = useState('');
  const [resentIds, setResentIds] = useState<Set<string>>(new Set());

  async function pickCourse(id: string) {
    setCourseId(id);
    setEmails(undefined);
    setEmailKey('');
    setRecipientData(undefined);
    if (!id) {
      return;
    }
    setIsLoading(true);
    try {
      setEmails(await rpx.emailDelivery.getDeliveryEmails({ courseId: id }));
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function pickEmail(key: string) {
    setEmailKey(key);
    setRecipientData(undefined);
    setSearch('');
    setResentIds(new Set());
    if (!key) {
      return;
    }
    setIsLoading(true);
    try {
      setRecipientData(await rpx.emailDelivery.getDeliveryRecipients({ courseId, emailKey: key }));
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function resend(userId: string, email: string) {
    setResendingId(userId);
    try {
      await rpx.emailDelivery.resendDeliveryEmail({ courseId, emailKey, userId });
      setResentIds((prev) => new Set(prev).add(userId));
      showToast({
        type: 'ok',
        title: 'Email sent',
        message: `Resent to ${email}.`,
      });
    } catch (err) {
      showError(err);
    } finally {
      setResendingId('');
    }
  }

  const term = search.trim().toLowerCase();
  const filteredRecipients = (recipientData?.recipients || []).filter(
    (r) =>
      !term || r.email.toLowerCase().includes(term) || (r.name || '').toLowerCase().includes(term),
  );
  const sentCount = (recipientData?.recipients || []).filter((r) => r.receiptAt).length;
  const wasManualSend = !!recipientData?.messageSentAt;

  // RPC dates arrive as strings — coerce before comparing.
  type Row = Recipients['recipients'][0];
  function deliveryStatus(r: Row) {
    if (r.receiptAt) {
      return { kind: 'sent' as const, at: r.receiptAt };
    }
    const firstSentAt = recipientData?.firstSentAt;
    if (!firstSentAt) {
      return { kind: 'none' as const };
    }
    // Access Opens only ever targeted deferred buyers; everyone else
    // (immediate buyers, free/invited enrollees) was never in the audience.
    if (recipientData?.isAccessOpens && !r.wasDeferred) {
      return { kind: 'notInAudience' as const };
    }
    if (new Date(r.enrolledAt) > new Date(firstSentAt)) {
      return { kind: 'enrolledLater' as const };
    }
    if (wasManualSend) {
      return { kind: 'manualSend' as const };
    }
    // In the audience before the send, but no receipt: the actionable case.
    return { kind: 'possiblyMissed' as const };
  }

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="emailDelivery" />
        <AdminTabWrapper>
          <AdminTabHeader title="Email delivery" />

          <div class="flex gap-2 mb-6 border-b border-gray-200">
            <Button
              class={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === 'course'
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab('course')}
            >
              Course emails
            </Button>
            <Button
              class={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === 'renewal'
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab('renewal')}
            >
              Renewal reminders
            </Button>
            <Button
              class={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === 'reminderCopy'
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab('reminderCopy')}
            >
              Reminder copy
            </Button>
          </div>

          {tab === 'renewal' && (
            <RenewalReminders cohorts={state.renewalCohorts} loadError={state.loadError} />
          )}

          {tab === 'reminderCopy' && <ReminderCopy />}

          {tab === 'course' && (
            <>
              <p class="text-sm text-gray-500 mb-6 max-w-2xl">
                See who received a course email and resend it to individual students. Students who
                enrolled after a send, or who already had access (e.g. AI Playground members joining
                a cohort for free), were never in the original audience — "not sent" is expected for
                them.
              </p>

              <div class="flex flex-wrap gap-4 mb-6">
                <label class="flex flex-col gap-1 text-sm font-medium">
                  Course
                  <select
                    class="border border-gray-300 rounded-md px-3 py-2 min-w-72"
                    value={courseId}
                    onChange={(e: any) => pickCourse(e.target.value)}
                  >
                    <option value="">Select a course…</option>
                    {state.courses.map((c: Courses[0]) => (
                      <option key={c.id} value={c.id}>
                        {c.title} ({c.studentCount} students)
                      </option>
                    ))}
                  </select>
                </label>

                {emails && (
                  <label class="flex flex-col gap-1 text-sm font-medium">
                    Email
                    <select
                      class="border border-gray-300 rounded-md px-3 py-2 min-w-72"
                      value={emailKey}
                      onChange={(e: any) => pickEmail(e.target.value)}
                    >
                      <option value="">Select an email…</option>
                      <option value={ACCESS_OPENS_KEY}>
                        Access Opens (system) — {emails.accessOpens.receiptCount} sent
                      </option>
                      {emails.messages.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title} —{' '}
                          {m.sentAt
                            ? m.receiptCount
                              ? `${m.receiptCount} sent + manual send`
                              : 'sent manually'
                            : `${m.receiptCount} sent`}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              {isLoading && (
                <div class="flex justify-center py-12">
                  <Spinner />
                </div>
              )}

              {recipientData && !isLoading && (
                <>
                  <div class="flex flex-wrap items-center gap-4 mb-4">
                    <input
                      type="search"
                      placeholder="Search name or email…"
                      class="border border-gray-300 rounded-md px-3 py-2 w-72"
                      value={search}
                      onInput={(e: any) => setSearch(e.target.value)}
                    />
                    <span class="text-sm text-gray-500">
                      {sentCount} of {recipientData.recipients.length} students have a delivery
                      receipt.
                      {wasManualSend &&
                        ` This message was sent manually on ${compactDate(
                          recipientData.messageSentAt!,
                        )} — manual sends don't record per-person receipts.`}
                    </span>
                  </div>

                  <table class="w-full table-fixed text-sm border-t border-gray-200">
                    <thead>
                      <tr class="text-left text-gray-500">
                        <th class="py-2 pr-4 font-medium w-[44%]">Student</th>
                        <th class="py-2 pr-4 font-medium w-[14%]">Enrolled</th>
                        <th class="py-2 pr-4 font-medium w-[28%]">Delivery</th>
                        <th class="py-2 font-medium w-[14%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecipients.map((r) => (
                        <tr key={r.userId} class="border-t border-gray-100">
                          <td class="py-2 pr-4">
                            <div class="flex items-baseline gap-2">
                              <span
                                class="font-medium truncate shrink-0 max-w-[60%]"
                                title={r.name || r.email}
                              >
                                {r.name || '—'}
                              </span>
                              <span class="text-gray-500 truncate" title={r.email}>
                                {r.email}
                              </span>
                            </div>
                          </td>
                          <td class="py-2 pr-4 whitespace-nowrap">{compactDate(r.enrolledAt)}</td>
                          <td class="py-2 pr-4 whitespace-nowrap">
                            {(() => {
                              const status = deliveryStatus(r);
                              switch (status.kind) {
                                case 'sent':
                                  return (
                                    <span class="text-green-700">
                                      Sent {compactDate(status.at)}
                                    </span>
                                  );
                                case 'manualSend':
                                  return <span class="text-gray-600">Included in manual send</span>;
                                case 'possiblyMissed':
                                  return (
                                    <span class="text-amber-600 font-medium">Possibly missed</span>
                                  );
                                case 'notInAudience':
                                  return (
                                    <span class="text-gray-400">Not in audience (had access)</span>
                                  );
                                case 'enrolledLater':
                                  return (
                                    <span class="text-gray-400">
                                      Not in audience (joined later)
                                    </span>
                                  );
                                default:
                                  return <span class="text-gray-400">Not sent</span>;
                              }
                            })()}
                          </td>
                          <td class="py-2 text-right">
                            <Button
                              class="text-indigo-600 hover:text-indigo-800 text-sm font-medium disabled:opacity-50"
                              disabled={resendingId === r.userId}
                              onClick={() => resend(r.userId, r.email)}
                            >
                              {resendingId === r.userId
                                ? 'Sending…'
                                : resentIds.has(r.userId)
                                  ? 'Sent ✓ (send again)'
                                  : r.receiptAt
                                    ? 'Resend'
                                    : 'Send'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!filteredRecipients.length && (
                        <tr>
                          <td colSpan={4} class="py-8 text-center text-gray-400">
                            No students match.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}
